/**
 * workflow-graph.tsx
 *
 * React Flow island for visualising Keystone workflow definitions.
 *
 * Mount point: <div id="workflow-graph" data-definition="<JSON>"></div>
 *
 * The server injects the workflow definition as a JSON string in the
 * data-definition attribute. This island reads it, transforms to nodes/edges,
 * and renders the graph.
 *
 * Execution overlay (phase 2): if data-execution="<JSON>" is also present,
 * steps are coloured by their current execution status.
 */

import { useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import {
  definitionToGraph,
  type WorkflowDefinition,
  type GraphNodeData,
} from "./definition-to-graph.js";

// ─── Custom node component ────────────────────────────────────────────────────

const KIND_ICON: Record<string, string> = {
  evaluate: "⚡",
  update_entity: "✏️",
  wait_for_decision: "⏳",
  pass: "✓",
  fail: "✗",
};

// Execution status colours (overlay, phase 2)
const EXEC_STATUS_COLORS: Record<string, string> = {
  started: "#2563eb",
  succeeded: "#16a34a",
  failed: "#dc2626",
  skipped: "#9ca3af",
};

function KeystoneNode({ data }: NodeProps) {
  const d = data as unknown as GraphNodeData;
  const bg = d.execStatus != null ? (EXEC_STATUS_COLORS[d.execStatus] ?? d.color) : d.color;
  const icon = KIND_ICON[d.kind] ?? "•";

  return (
    <div
      style={{
        background: bg,
        color: d.textColor,
        borderRadius: 10,
        minWidth: 240,
        maxWidth: 300,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        fontFamily: "system-ui, sans-serif",
        fontSize: 13,
        overflow: "hidden",
      }}
    >
      {/* Target handle (top) */}
      <Handle type="target" position={Position.Top} style={{ background: "rgba(255,255,255,0.6)" }} />

      {/* Header */}
      <div
        style={{
          padding: "8px 14px 6px",
          borderBottom: "1px solid rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontWeight: 700, flex: 1, wordBreak: "break-all" }}>{d.label}</span>
        {d.isGuarded && (
          <span
            title={`when: ${d.when ?? ""}`}
            style={{
              background: "rgba(255,255,255,0.25)",
              borderRadius: 4,
              padding: "1px 6px",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            guarded
          </span>
        )}
      </div>

      {/* Kind badge */}
      <div style={{ padding: "4px 14px 2px", opacity: 0.85, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {d.kind.replace(/_/g, " ")}
      </div>

      {/* Detail lines (inputs/outputs) */}
      {d.details.length > 0 && (
        <div style={{ padding: "4px 14px 10px" }}>
          {d.details.map((line: string, i: number) => (
            <div
              key={i}
              style={{
                background: "rgba(0,0,0,0.12)",
                borderRadius: 4,
                padding: "2px 6px",
                marginTop: 3,
                fontSize: 11,
                fontFamily: "monospace",
                wordBreak: "break-all",
              }}
            >
              {line}
            </div>
          ))}
        </div>
      )}

      {/* Source handle (bottom) */}
      <Handle type="source" position={Position.Bottom} style={{ background: "rgba(255,255,255,0.6)" }} />
    </div>
  );
}

const nodeTypes = { keystoneNode: KeystoneNode };

// ─── Root app ─────────────────────────────────────────────────────────────────

interface AppProps {
  definition: WorkflowDefinition;
  execution?: Record<string, string>; // stepName → execStatus (phase 2)
}

function WorkflowGraphApp({ definition, execution }: AppProps) {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const graph = definitionToGraph(definition);

    // Phase 2: overlay execution status on nodes
    if (execution != null) {
      for (const node of graph.nodes) {
        const status = execution[node.id];
        if (status != null) {
          node.data = { ...node.data, execStatus: status };
        }
      }
    }

    return graph;
  }, [definition, execution]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes as any);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges as any);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        maxZoom={2}
        proOptions={{ hideAttribution: false }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e5e7eb" />
        <Controls />
        <MiniMap
          nodeColor={(n) => {
            const d = n.data as unknown as GraphNodeData;
            return d.execStatus != null ? (EXEC_STATUS_COLORS[d.execStatus] ?? d.color) : d.color;
          }}
          maskColor="rgba(249,250,251,0.7)"
          style={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
        />
      </ReactFlow>
    </div>
  );
}

// ─── Mount ────────────────────────────────────────────────────────────────────

export function mountWorkflowGraph() {
  const el = document.getElementById("workflow-graph");
  if (!el) return;

  const rawDef = el.getAttribute("data-definition");
  if (!rawDef) return;

  let definition: WorkflowDefinition;
  try {
    const parsed: unknown = JSON.parse(rawDef);
    // Defensive: if the server somehow injected a JSON-encoded string
    // instead of an object, unwrap one more time so we render instead of
    // crashing in definitionToGraph on a missing `.steps` array. The
    // server-side coerceDefinition helper should make this unnecessary,
    // but keeping it here means a misconfigured row doesn't black-screen
    // the page.
    const unwrapped: unknown = typeof parsed === "string" ? JSON.parse(parsed) : parsed;
    if (
      unwrapped == null ||
      typeof unwrapped !== "object" ||
      Array.isArray(unwrapped) ||
      !Array.isArray((unwrapped as { steps?: unknown }).steps)
    ) {
      el.textContent =
        "Error: workflow definition is malformed (expected an object with a 'steps' array).";
      return;
    }
    definition = unwrapped as WorkflowDefinition;
  } catch {
    el.textContent = "Error: could not parse workflow definition.";
    return;
  }

  const rawExec = el.getAttribute("data-execution");
  let execution: Record<string, string> | undefined;
  if (rawExec != null) {
    try {
      execution = JSON.parse(rawExec) as Record<string, string>;
    } catch {
      // ignore, just skip overlay
    }
  }

  // Lazy-import React DOM to keep the bundle split clean
  import("react-dom/client").then(({ createRoot }) => {
    const root = createRoot(el);
    // exactOptionalPropertyTypes: spread to avoid undefined-vs-missing mismatch
    const extraProps = execution != null ? { execution } : {};
    root.render(<WorkflowGraphApp definition={definition} {...extraProps} />);
  });
}
